import md5 from 'md5';

/**
 * Hashes an opaque secret — a capability token, a signed link id — that is only
 * ever needed as a join key across events, never read back.
 *
 * Lives here rather than next to a single app's trackings because both ends of
 * a token's lifecycle report it: the authenticated portal that mints the token
 * and the unauthenticated page the recipient opens. Those events only line up
 * in Amplitude if both sides hash identically, so there is exactly one
 * implementation and neither app owns it.
 *
 * Do not swap in a hash helper that only masks values matching some shape (a
 * phone number, say) and passes everything else through. That would emit most
 * tokens in the clear while hashing the minority that happen to match — leaking
 * the secret and breaking correlation for the same field.
 *
 * MD5 is fine here where it would not be for a phone number: the input is high
 * entropy, so there is no small domain to enumerate back. Truncated to 16 hex
 * characters (64 bits) to match the `ph_` convention, which is far more than
 * enough to keep a join key collision-free.
 */
export function hashTrackingToken(
  token: string | null | undefined,
): string | undefined {
  // Empty counts as absent. Hashing '' succeeds and yields a fixed string that
  // looks exactly like a real join key, so every event that had no token would
  // share one and appear to belong to the same request.
  if (!token) {
    return undefined;
  }

  return `tok_${md5(token).substring(0, 16)}`;
}
